/**
 * The world screen (D245: the whole loop on one screen). The map, where every producer
 * owned stands on the land, and under it one row per producer with its bar, its buy
 * button, its hand and its next milestone, then the next goal or two. The map keeps about
 * 38% of the screen however long the list grows (D259); the list scrolls under it.
 */
import { Fragment, type ComponentChildren } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  idleBonusOffers,
  idleDayPhase,
  idleLongGoalsMet,
  idleMilestonesReached,
  idleProducer,
  idleSeason,
} from '@hedgerow/sim';
import { now } from '../clock.ts';
import {
  celebrateMilestone,
  flyCoins,
  NoticeHost,
  notify,
  playSound,
  popFromCorner,
  popNumber,
} from '../juice/index.ts';
import { IsoWorldMap } from '../ui/IsoWorldMap.tsx';
import { mapKitFor } from '../ui/iso-kit.ts';
import { Button } from '../ui/Button.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Segmented } from '../ui/Segmented.tsx';
import { worldTiers, idleScene } from './map.ts';
import { ArrangeDock } from './ArrangeUI.tsx';
import { DecorationsSheet } from './DecorationsSheet.tsx';
import { FarmSheet } from './FarmSheet.tsx';
import { useShownWorld } from './shown-world.ts';
import { LazyThreeWorld } from './three/LazyThreeWorld.tsx';
import { useFlatMap, useShowSigns } from './three/pref.ts';
import { gain } from './notation.ts';
import { cap, t as term } from './terms.ts';
import {
  BUY_MODES,
  BUY_MODE_LABEL,
  closeCircleTip,
  countCircleTap,
  useBuyMode,
  useCircleTip,
} from './buy-switch.ts';
import { ChainRowView, LockedRowView, ProducerRowView } from './ProducerRow.tsx';
import { idleContent, useIdle } from './store.ts';
import {
  activeBonuses,
  chainRows,
  findSpot,
  landSections,
  lockedRows,
  tierOrder,
  aTerm,
  milestoneToast,
  producerRows,
  refusalText,
  tipApplies,
  tipFor,
  tipText,
  type ChainRow,
  type LockedRow,
  type ProducerRow,
  type TipId,
  goalMetText,
} from './view.ts';

/** Taps closer together than this count as one run, and the coin note climbs. */
const COMBO_GAP_MS = 4000;

/** Every control on the world screen is at least this tall and wide (D261). */
export const TAP_MIN = 44;

/** A merged notice's count (D374): the number its words start with, else one. */
const count = (text: string): number => Number(/^(\d+) /.exec(text)?.[1] ?? 1);

export function WorldTab({
  prompts,
  moreOffers,
}: {
  prompts?: ComponentChildren;
  moreOffers?: ComponentChildren;
}) {
  const world = useIdle((s) => s.world);
  const tickNow = useIdle((s) => s.now);
  const tipsSeen = useIdle((s) => s.tipsSeen);
  const goalsMet = useIdle((s) => s.goalsMet);
  // No tip and no notice while a fanfare is open: the fanfare has the screen to itself.
  const fanfare = useIdle((s) => !!s.welcome || !!s.festival || !!s.passedOn);
  const markPulse = useIdle((s) => s.markPulse);
  // D292: photo mode hides the rest of the screen; the 3D lane reads the same flag.
  const photoMode = useIdle((s) => s.photoMode);
  // D331: the Decorations sheet under the map, and the world drawn with what is tried on.
  const decorOpen = useIdle((s) => s.decorOpen);
  const farmOpen = useIdle((s) => s.farmOpen);
  // D454 step 3: arrange mode takes the sheet's place with its dock.
  const arranging = useIdle((s) => !!s.arrange);
  const shown = useShownWorld();
  const combo = useRef({ at: 0, n: 0 });
  // D283: the 3D world is the default; "Flat map" in settings opts back out.
  const [flatMapOn] = useFlatMap();
  const [showSigns] = useShowSigns();
  const [buyMode, setBuyMode] = useBuyMode();
  const tipShown = useCircleTip();

  // D259: just after a festival, the new mark is drawn going down.
  const pulse = markPulse && tickNow < markPulse.until ? markPulse.index : undefined;
  // D268: a producer whose tier goes up plays its moment on its own plot for a short while.
  const tiers = useRef<Record<string, number> | null>(null);
  const [burst, setBurst] = useState<{ id: string; until: number } | null>(null);
  useEffect(() => {
    if (!world) return;
    const next = worldTiers(world, idleContent.theme.id);
    const was = tiers.current;
    tiers.current = next;
    if (!was) return;
    const up = Object.keys(next).find((id) => (next[id] ?? 0) > (was[id] ?? 0));
    if (up) setBurst({ id: up, until: now() + 2000 });
  }, [world]);
  const burstId = burst && tickNow < burst.until ? burst.id : undefined;
  // D365: a producer or building row that has shown this year stays shown even past a dip
  // under a quarter of its price. `seenRows` is kept per year in the store; this effect adds
  // to it whatever the threshold alone has just revealed.
  const seenRows = useIdle((s) => s.seenRows);
  const locked = world ? lockedRows(world, idleContent, seenRows) : [];
  const chains = world ? chainRows(world, idleContent, seenRows) : [];
  useEffect(() => {
    if (!world) return;
    const year = world.year.number;
    const { seeRow } = useIdle.getState();
    for (const r of locked) seeRow(r.id, year);
    for (const c of chains) seeRow(c.id, year);
  }, [world?.year.number, locked.map((r) => r.id).join(','), chains.map((c) => c.id).join(',')]);
  const scene = useMemo(
    () =>
      shown
        ? idleScene(shown, tickNow, {
            // D348: the stored theme, as the server sent it, never guessed.
            theme: idleContent.theme.id,
            ...(pulse === undefined ? {} : { pulseMark: pulse }),
            ...(burstId ? { burst: burstId } : {}),
          })
        : null,
    [shown, tickNow, pulse, burstId],
  );

  // D261: a tip is counted seen the moment it shows, and stays while its subject is on
  // screen or until it is closed. It never comes back.
  const [tip, setTip] = useState<TipId | null>(null);
  const wanted = world && !fanfare && !tip ? tipFor(world, idleContent, tipsSeen) : null;
  useEffect(() => {
    if (!wanted) return;
    useIdle.getState().seeTip(wanted);
    setTip(wanted);
  }, [wanted]);
  useEffect(() => {
    if (tip && world && !tipApplies(tip, world, idleContent)) setTip(null);
  }, [tip, world]);

  // D260: a goal met is a banner in the one queue, shown on the World tab only.
  useEffect(() => {
    if (!goalsMet.length || fanfare) return;
    for (const key of goalsMet) {
      // D334: a key names a goal's tier; its words carry the tier's target.
      const said = world ? goalMetText(idleContent, world, key) : null;
      const openId = useIdle.getState().worldId;
      notify({
        kind: 'banner',
        testId: 'goal-met',
        text: `Goal met: ${said?.name ?? key}`,
        ...(said ? { sub: said.reward } : {}),
        sound: 'milestone',
        coins: 8,
        // D374: most important first; several together read as one.
        priority: 3,
        group: 'goal',
        join: (a, b) => ({
          text: `${count(a.text) + count(b.text)} goals met`,
          sub: `Open ${term('tabAlmanac')}`,
        }),
        // D374: worded in the open world's names and still met, as it reaches the screen.
        check: () => {
          const s = useIdle.getState();
          if (!s.world || s.worldId !== openId) return null;
          if (!s.world.year.goals.includes(key) && !idleLongGoalsMet(s.world).includes(key))
            return null;
          const now = goalMetText(idleContent, s.world, key);
          return now ? { text: `Goal met: ${now.name}`, sub: now.reward } : null;
        },
      });
    }
    useIdle.getState().takeGoals();
  }, [goalsMet, fanfare]);

  // D340: a tap on a producer's plot or pen, or a chain's building, scrolls the list to its
  // row and briefly rings it there. Bare land still rings on the map, but nothing to scroll
  // to. The flash is a plain DOM touch rather than state, so it never fights the list's own
  // re-renders while the world ticks.
  const flashRow = (rowId: string) => {
    const el = document.querySelector<HTMLElement>(
      `[data-testid="producer-${rowId}"], [data-testid="chain-${rowId}"], [data-testid="locked-${rowId}"]`,
    );
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.dataset.flash = 'true';
    el.style.outline = '3px solid var(--accent)';
    el.style.outlineOffset = '2px';
    el.style.transition = 'outline-color 1.1s ease-out';
    window.setTimeout(() => {
      delete el.dataset.flash;
      el.style.outline = '';
      el.style.outlineOffset = '';
      el.style.transition = '';
    }, 1200);
  };
  // D340: the flat map's own selection ring is a controlled tile key; a second tap on the
  // same tile clears it, as the 3D map's tap-to-deselect does.
  const [flatSel, setFlatSel] = useState<string | null>(null);
  const onFlatSelect = (t: { x: number; y: number }, rowId: string | undefined) => {
    const key = `${t.x},${t.y}`;
    setFlatSel((cur) => (cur === key ? null : key));
    if (rowId) flashRow(rowId);
  };
  // D345: the flat map's own "ready" icon collects exactly as the row's Tap does, with
  // coins flying from the field. The 3D map does the same thing itself (it already holds
  // `useIdle`); this is the flat map's own hook, which does not.
  // D353: the coins fly from the tapped field only. D354: an idle crop's first tap starts it,
  // as the row's own Tap does: a tap's sound, no coins.
  const onFlatCollect = (producer: string, at: { clientX: number; clientY: number }) => {
    const worldNow = useIdle.getState().world;
    const before = worldNow?.year.coins ?? 0;
    const idle = !worldNow?.year.producers[producer]?.running;
    const r = useIdle.getState().act({ type: 'tap', producer });
    if (!r.ok) return;
    if (idle) return playSound('tap');
    playSound('coin', { combo: 3 });
    popNumber(at, gain(r.state.year.coins - before));
    flyCoins(at, { count: 6 });
  };

  if (!world || !scene) return null;
  // D340: still takes no pan or zoom, but still answers a tap (`onSelect`), so the flat map
  // reads and rings exactly as the live game does whether it is the fallback picture or the
  // player's own chosen renderer.
  const flatMap = (
    <IsoWorldMap
      still
      theme={scene.theme}
      // D360: the season and the hour, as the 3D world shows them. D353: the clock its worked
      // fields run their own cycle on.
      season={idleSeason(shown ?? world, idleContent, tickNow).season}
      time={mapKitFor(scene.theme).phaseTime(idleDayPhase(tickNow, idleContent).phase)}
      clock={tickNow}
      label={`Your ${term('world')}`}
      parcels={scene.parcels}
      buildings={scene.buildings}
      plot={scene.plot}
      worldPlot={scene.worldPlot}
      lands={scene.lands}
      country={scene.country}
      stream={scene.stream}
      showSigns={showSigns}
      decor={scene.decor}
      selected={flatSel}
      onSelect={onFlatSelect}
      onCollect={onFlatCollect}
      style={{ position: 'absolute', inset: 0, height: '100%', aspectRatio: 'auto' }}
    />
  );
  const rows = producerRows(world, idleContent);
  // D301: the world's rows, then each open piece of land's under its own heading.
  const groups = [{ id: '', name: '' }, ...landSections(world, idleContent)].map((g) => ({
    ...g,
    rows: rows.filter((r) => (r.land ?? '') === g.id),
    locked: locked.filter((r) => (r.land ?? '') === g.id),
  }));
  const boosted = !!activeBonuses(world, tickNow).market;
  const find = idleBonusOffers(world, idleContent, tickNow).find((o) => o.kind === 'lucky_find');

  const onFind = (el: Element) => {
    if (!find) return;
    const r = useIdle.getState().act({ type: 'claimBonus', offer: find.id });
    if (!r.ok) return refuse(el, refusalText(idleContent, r));
    playSound('coin', { combo: 4 });
    popNumber(el, gain(find.coins ?? 0));
    flyCoins(el, { count: 8 });
  };

  /** D374: an unlock or a build is coins and a sound; the map and the list already show it. */
  const celebrate = (el: Element) => {
    flyCoins(el, { count: 6 });
    playSound('milestone');
  };

  const refuse = (el: Element, text: string) => {
    playSound('error');
    popNumber(el, text);
  };

  // Coins fly from the tile's own box, wherever on it the tap landed (D260).
  const onTap = (row: ProducerRow, el: Element) => {
    const working = row.hand || (row.running && el.getAttribute('data-state') !== 'ready');
    const r = useIdle.getState().act({ type: 'tap', producer: row.id });
    // D454: three taps on a circle and the first-time tip goes.
    countCircleTap();
    if (working) {
      // D277: a tap on a working crop speeds the cycle under way. Where the world cannot take
      // it, the tap does nothing rather than scold.
      if (!r.ok) return;
      playSound('tap');
      popFromCorner(el, 'Faster');
      const bar = el.closest('[data-testid^="producer-"]')?.querySelector('.idle-ring');
      if (bar) {
        bar.classList.remove('idle-bar-boost');
        void (bar as HTMLElement).offsetWidth;
        bar.classList.add('idle-bar-boost');
      }
      return;
    }
    if (!r.ok) return refuse(el, refusalText(idleContent, r, row.id));
    if (row.idle) {
      playSound('tap');
      return;
    }
    const t = now();
    combo.current = {
      at: t,
      n: t - combo.current.at < COMBO_GAP_MS ? combo.current.n + 1 : 0,
    };
    popFromCorner(el, gain(row.tapPayout));
    flyCoins(el, { count: 12 });
    playSound('coin', { combo: combo.current.n });
  };

  const onBuy = (row: ProducerRow, n: number, el: Element) => {
    const p = idleProducer(idleContent, row.id);
    const before = p ? idleMilestonesReached(p, row.owned).length : 0;
    const r = useIdle.getState().act({ type: 'buy', producer: row.id, n });
    if (!r.ok) return refuse(el, refusalText(idleContent, r, row.id));
    playSound('buy');
    popNumber(el, `+${n}`);
    const owned = r.state.year.producers[row.id]?.owned ?? row.owned;
    const reached = p ? idleMilestonesReached(p, owned) : [];
    const m = reached[reached.length - 1];
    if (p && m && reached.length > before) {
      // D431: the ring, bar and sign on the row; they follow the Settings switch (D305).
      const rowEl = el.closest('[data-testid^="producer-"]');
      if (rowEl) celebrateMilestone(rowEl, { text: String(m.owned), signs: showSigns });
      const { worldId: openId, world: f } = useIdle.getState();
      const year = f?.year.number;
      notify({
        kind: 'toast',
        testId: 'milestone-toast',
        text: milestoneToast(row.name, m),
        sound: 'milestone',
        priority: 1,
        ms: 3600,
        group: 'milestone',
        join: (a, b) => ({ text: `${count(a.text) + count(b.text)} milestones reached` }),
        // D374: still the latest milestone of this producer in this year of this world.
        check: () => {
          const s = useIdle.getState();
          const now = idleProducer(idleContent, row.id);
          if (!now || !s.world || s.worldId !== openId || s.world.year.number !== year) return null;
          const last = idleMilestonesReached(now, s.world.year.producers[row.id]?.owned ?? 0).at(
            -1,
          );
          return last && last.owned === m.owned ? { text: milestoneToast(now.name, last) } : null;
        },
      });
    }
  };

  const onHire = (row: ProducerRow, el: Element) => {
    const r = useIdle.getState().act({ type: 'hireHand', producer: row.id });
    if (!r.ok) return refuse(el, refusalText(idleContent, r, row.id));
    playSound('hire');
    popNumber(el, `${cap(term('hand'))} hired`);
  };

  const onUnlock = (row: LockedRow, el: Element) => {
    const r = useIdle.getState().act({ type: 'unlock', producer: row.id });
    if (!r.ok) return refuse(el, refusalText(idleContent, r, row.id));
    celebrate(el);
  };

  const onBuild = (row: ChainRow, el: Element) => {
    const r = useIdle.getState().act({ type: 'buildChain', chain: row.id });
    if (!r.ok) return refuse(el, refusalText(idleContent, r, row.producer));
    celebrate(el);
  };

  return (
    <div
      className="idle-worldtab"
      data-screen-label={term('tabWorld')}
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      {/* D259: the map keeps at least about 38% of the screen however long the list; in
          photo mode (D292) it takes the lot and the rest of the screen is hidden. */}
      <div
        className="idle-map"
        data-testid="world-map"
        style={
          photoMode
            ? { position: 'relative', flex: '1 1 auto' }
            : { position: 'relative', flex: '1 0 38vh', minHeight: '38vh' }
        }
      >
        {flatMapOn ? (
          flatMap
        ) : (
          <LazyThreeWorld
            label={`Your ${term('world')}`}
            parcels={scene.parcels}
            buildings={scene.buildings}
            plot={scene.plot}
            worldPlot={scene.worldPlot}
            lands={scene.lands}
            country={scene.country}
            stream={scene.stream}
            showSigns={showSigns}
            style={{ position: 'absolute', inset: 0 }}
            fallback={flatMap}
            onSelect={flashRow}
          />
        )}
        {!photoMode && !arranging && find && (
          <LuckyFind key={find.id} id={find.id} until={find.until} now={tickNow} onTap={onFind} />
        )}
        {!photoMode && <NoticeHost paused={fanfare} />}
        {/* D457: first-time tips are dark overlays over the map's foot, above the sheet, so
            closing one moves nothing in the list. A pointer runs down to the circles. */}
        {!photoMode && !arranging && !fanfare && (tip || tipShown) && (
          <div
            data-testid="tips-stack"
            style={{
              position: 'absolute',
              left: 12,
              right: 56,
              // Clear of the N more offers button, which sits centred at the map's foot.
              bottom: SHEET_OVERLAP + (moreOffers ? 62 : 10),
              zIndex: 3,
              display: 'grid',
              gap: 6,
            }}
          >
            {tip && (
              <TipBubble testId={`tip-${tip}`} onClose={() => setTip(null)}>
                {tipText(tip, idleContent)}
              </TipBubble>
            )}
            {tipShown && (
              <TipBubble testId="circle-tip" closeId="circle-tip-close" onClose={closeCircleTip}>
                {cap(term('tap'))} a circle to {term('harvest')} it. {cap(aTerm(term('hand')))} does
                it for you.
              </TipBubble>
            )}
          </div>
        )}
        {/* D431: the Next and Bonus pills sit on the map, 10px from its top. They start right of
            the 3D map's compass until that moves to the bottom left (the map's own file). */}
        {!photoMode && !arranging && prompts && (
          <div
            class="idle-prompts"
            style={{
              position: 'absolute',
              top: 10,
              left: PROMPT_LEFT,
              right: 12,
              display: 'grid',
              gap: 6,
              zIndex: 2,
            }}
          >
            {prompts}
          </div>
        )}
        {/* D292: a photo of the world, interface hidden, framed by the 3D lane. */}
        {photoMode ? (
          <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6 }}>
            {/* The test ids sit on a wrapper: `Button` passes no data attributes through. */}
            <div data-testid="idle-photo-close">
              <Button onClick={() => useIdle.getState().exitPhotoMode()}>Close</Button>
            </div>
          </div>
        ) : (
          <MapMore hidden={decorOpen || farmOpen || arranging} moreOffers={moreOffers} />
        )}
      </div>
      {!photoMode && decorOpen && <DecorationsSheet />}
      {!photoMode && farmOpen && <FarmSheet />}
      {!photoMode && arranging && <ArrangeDock />}
      {!photoMode && !decorOpen && !arranging && (
        <div
          class="idle-sheet"
          data-testid="world-sheet"
          style={{
            position: 'relative',
            zIndex: 1,
            marginTop: -SHEET_OVERLAP,
            flex: '0 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg)',
            borderRadius: '12px 12px 0 0',
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.12)',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              flex: 'none',
              width: 36,
              height: 4,
              borderRadius: 2,
              background: 'var(--line)',
              margin: '6px auto 6px',
            }}
          />
          <div
            style={{
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '2px var(--edge) 8px',
              font: '600 12px/1 var(--font-ui)',
              color: 'var(--ink-2)',
            }}
          >
            <span style={{ display: 'grid', gap: 4 }}>
              <span>Producers</span>
              <span class="tnum" data-testid="sheet-count">
                {rows.length} of {idleContent.producers.length} open
              </span>
            </span>
            {/* D454: how many each row's Buy buys, one switch for the list. */}
            <span data-testid="count-switch" aria-label="How many to buy">
              <Segmented
                variant="standalone"
                value={buyMode}
                onChange={setBuyMode}
                options={BUY_MODES.map((m) => ({ value: m, label: BUY_MODE_LABEL[m] }))}
              />
            </span>
          </div>
          <div
            data-testid="world-rows"
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              display: 'grid',
              alignContent: 'start',
              // Each row is as tall as it is: a grid squeezed to scroll keeps it so.
              gridAutoRows: 'max-content',
              gap: 6,
              padding: '0 var(--edge) 12px',
            }}
            class={boosted ? 'idle-rows idle-boosted' : 'idle-rows'}
          >
            {/* D285: the one prompt bar for the festival, Heritage to spend and an upgrade ready
              now lives in `NextStrip`, under the header, fed by `worldPrompt`. */}
            {groups.map((g) => (
              <Fragment key={g.id || 'world'}>
                {g.name && (
                  <h3
                    data-testid={`land-${g.id}`}
                    style={{ margin: '8px 0 0', fontSize: 'var(--fs-name)', fontWeight: 600 }}
                  >
                    {g.name}
                  </h3>
                )}
                {tierOrder(idleContent, g.rows, g.locked).map((item) =>
                  item.kind === 'locked' ? (
                    <LockedRowView key={item.row.id} row={item.row} onUnlock={onUnlock} />
                  ) : (
                    <Fragment key={item.row.id}>
                      <ProducerRowView
                        row={item.row}
                        mode={buyMode}
                        onTap={onTap}
                        onBuy={onBuy}
                        onHire={onHire}
                      />
                      {chains
                        .filter((c) => c.producer === item.row.id)
                        .map((c) => (
                          <ChainRowView key={c.id} row={c} onBuild={onBuild} />
                        ))}
                    </Fragment>
                  ),
                )}
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** D431: the sheet rides this far up over the map. */
const SHEET_OVERLAP = 22;

/** D457: a dark bubble (ink fill, surface text, 12px radius, a 44px Close) with a pointer down. */
function TipBubble({
  testId,
  closeId,
  onClose,
  children,
}: {
  testId: string;
  closeId?: string;
  onClose: () => void;
  children: ComponentChildren;
}) {
  return (
    <div
      role="note"
      data-testid={testId}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '0 0 0 12px',
        borderRadius: 12,
        background: 'var(--ink)',
        color: 'var(--surface)',
        font: '500 13px/1.35 var(--font-ui)',
        boxShadow: '0 2px 8px rgba(0,0,0,.25)',
      }}
    >
      <span style={{ flex: 1, minWidth: 0, padding: '6px 0' }}>{children}</span>
      <button
        type="button"
        aria-label="Close"
        data-testid={closeId}
        onClick={onClose}
        style={{
          flex: 'none',
          width: 44,
          height: 44,
          display: 'grid',
          placeItems: 'center',
          border: 0,
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        <Icon name="x" size={18} />
      </button>
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 28,
          bottom: -6,
          width: 12,
          height: 12,
          background: 'var(--ink)',
          transform: 'rotate(45deg)',
        }}
      />
    </div>
  );
}
/** D431, D435: the Next pill's left edge, 12px in now the compass sits at the bottom left. */
const PROMPT_LEFT = 12;

/**
 * D442: the map's foot, over the sheet's overlap. The overflow button at the right is one 44px
 * icon button that opens the Decorations and photo sheet; the "N more offers" button (when
 * there are any) sits in the middle, between it and the compass at the left. The test id stays
 * on the wrapper the specs find the button through.
 */
function MapMore({ hidden, moreOffers }: { hidden: boolean; moreOffers?: ComponentChildren }) {
  if (hidden) return null;
  return (
    <>
      {moreOffers && (
        <div
          data-testid="map-foot"
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: SHEET_OVERLAP + 8,
            zIndex: 2,
          }}
        >
          {moreOffers}
        </div>
      )}
      <div
        data-testid="idle-decorations"
        style={{ position: 'absolute', right: 8, bottom: SHEET_OVERLAP + 8, zIndex: 2 }}
      >
        <button
          type="button"
          data-testid="map-more"
          aria-label={`${cap(term('world'))} options`}
          onClick={() => useIdle.getState().setFarmOpen(true)}
          class="idle-chrome"
          style={{
            width: 44,
            height: 44,
            padding: 0,
            border: '1px solid rgba(255, 255, 255, 0.18)',
            borderRadius: 'var(--radius-md)',
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
        </button>
      </div>
    </>
  );
}

/** Under this long left, a lucky find starts to fade. */
const FIND_FADE_MS = 6000;

/**
 * A lucky find (D244): a small glinting thing lying on the world. It chimes when it appears,
 * pays when tapped, and fades away if it is left. Nothing is lost when it goes.
 */
function LuckyFind({
  id,
  until,
  now: t,
  onTap,
}: {
  id: string;
  until: number;
  now: number;
  onTap: (el: Element) => void;
}) {
  useEffect(() => {
    playSound('tap');
  }, []);
  const spot = findSpot(id);
  const fading = until - t < FIND_FADE_MS;
  return (
    <button
      type="button"
      data-testid="lucky-find"
      aria-label={`${term('luckyFind')}. Tap to pick it up`}
      class={fading ? 'idle-find idle-find-fading' : 'idle-find'}
      onClick={(e) => onTap(e.currentTarget)}
      style={{
        position: 'absolute',
        left: `${spot.left * 100}%`,
        top: `${spot.top * 100}%`,
        width: TAP_MIN,
        height: TAP_MIN,
        marginLeft: -TAP_MIN / 2,
        marginTop: -TAP_MIN / 2,
        borderRadius: '50%',
        border: 0,
        padding: 0,
        background: 'transparent',
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'var(--coin)',
          border: '2px solid var(--coin-edge)',
          boxShadow: '0 1px 3px rgba(0,0,0,.3)',
        }}
      />
    </button>
  );
}
